import React, { useEffect, useState } from "react";
import { Card, CardBody, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FloorService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import DeleteModal from "../../Common/DeleteModal";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { usePagination } from "../../shared/hooks/usePagination";
import { CustomPagination } from "../../shared/components/CustomPagination";
import { entriesPerPageOptions } from "../customers/List";
import { IFloor } from "../../shared/interfaces/floor/IFloor";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const PER_PAGE_DEFAULT = 20;

interface SortableRowProps {
    item: IFloor;
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
    t: (key: string) => string;
}

const SortableRow = ({ item, onEdit, onDelete, t }: SortableRowProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id! });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr ref={setNodeRef} style={style} {...attributes}>
            <td {...listeners} style={{ cursor: "grab" }}>
                <i className="ti ti-grip-vertical f-18"></i>
            </td>
            <td>{item.id}</td>
            <td>{item.name}</td>
            <td>{item.floorNumber}</td>
            <td>{new Date(item.updatedAt).toLocaleDateString()}</td>
            <td className="text-end">
                <ul className="list-inline mb-0">
                    <li
                        className="list-inline-item"
                        onClick={() => onEdit(item.id!)}
                    >
                        <a
                            href="#"
                            className="avtar avtar-s btn-link-success btn-pc-default"
                        >
                            <i className="ti ti-edit f-20"></i>
                        </a>
                    </li>
                    <li className="list-inline-item">
                        <span
                            role="button"
                            className="avtar avtar-s btn-link-danger btn-pc-default"
                            onClick={() => item.id && onDelete(item.id)}
                        >
                            <i className="ti ti-trash f-20"></i>
                        </span>
                    </li>
                </ul>
            </td>
        </tr>
    );
};

const FloorList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState<IFloor[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [floorToDelete, setFloorToDelete] = useState<number | null>(null);

  const {
    page,
    handlePages,
    pagination,
    totalPages,
    setTotalPages,
    handleUpdateList,
    changeFilterHandler,
    goToPreviousPage,
    goToNextPage,
  } = usePagination({
    perPage: PER_PAGE_DEFAULT,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && list) {
        const oldIndex = list.findIndex((item) => item.id === active.id);
        const newIndex = list.findIndex((item) => item.id === over.id);
        const newList = arrayMove(list, oldIndex, newIndex);
        setList(newList);
        try {
            await FloorService.sort(newList.map((item) => item.id!));
            SuccessToast(t("floor.sortSuccess"));
        } catch (error) {
            ErrorToast(error);
            fetchData();
        }
    }
  };

  const fetchData = () => {
    FloorService.getAll(pagination)
      .then((response) => {
        const data = response.data;
        setList(data);
        setTotalPages(response.totalPages);
      })
      .catch(ErrorToast);
  };

  const handleDeleteClick = (id: number) => {
    setFloorToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (floorToDelete !== null) {
      try {
        await FloorService.delete(floorToDelete);
        SuccessToast(t("floor.successDelete"));
        setShowDeleteModal(false);
        setFloorToDelete(null);
        handleUpdateList();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination]);

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody>
            <div className="text-end">
              <button
                className="btn btn-primary text-end"
                onClick={() => navigate("/floors/create")}
              >
                <i className="ti ti-plus f-18"></i> {t("floor.add")}
              </button>
            </div>

            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t("floor.entriesPerPage")}
                    options={entriesPerPageOptions}
                    onChange={(selected) =>
                      changeFilterHandler(
                        "perPage",
                        selected?.value ?? PER_PAGE_DEFAULT,
                      )
                    }
                    defaultValue={entriesPerPageOptions.find(
                      (option) => option.value === PER_PAGE_DEFAULT,
                    )}
                    classNamePrefix="react-select"
                  />
                </li>
                <span> {t("floor.entriesPerPage")}</span>
              </ul>
              <ul className="list-inline ms-auto my-1 mt-3">
                <li className="list-inline-item">
                  <form className="">
                    <input
                      className="form-control"
                      type="search"
                      placeholder={t("floor.searchPlaceholder")}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        changeFilterHandler("term", e.target.value)
                      }
                    />
                  </form>
                </li>
              </ul>
            </div>
            <div className="table-responsive">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="table table-hover" id="pc-dt-simple-1">
                  <thead>
                    <tr>
                      <th style={{ width: "50px" }}></th>
                      <th>{t("floor.id")}</th>
                      <th>{t("floor.name")}</th>
                      <th>{t("floor.floorNumber")}</th>
                      <th>{t("floor.updatedAt")}</th>
                      <th className="text-end">{t("floor.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list && list.length > 0 && (
                        <SortableContext
                            items={list.map((item) => item.id!)}
                            strategy={verticalListSortingStrategy}
                        >
                            {list.map((item: IFloor) => (
                                <SortableRow
                                    key={item.id}
                                    item={item}
                                    onEdit={(id) => navigate(`/floors/edit/${id}`)}
                                    onDelete={handleDeleteClick}
                                    t={t}
                                />
                            ))}
                        </SortableContext>
                    )}
                  </tbody>
                </table>
              </DndContext>
            </div>
            <CustomPagination
              page={page}
              totalPages={totalPages}
              handlePages={handlePages}
              goToPreviousPage={goToPreviousPage}
              goToNextPage={goToNextPage}
            />
          </CardBody>
        </Card>
      </Col>

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name={t("floor.name")}
      />
    </React.Fragment>
  );
};

export default FloorList;
